import { useCallback, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import moment from 'moment'
import HeroImage from 'content/components/HeroImage'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TripLandingPageHowItWorks from './TripLandingPageHowItWorks'
import TripLandingPageWhyYoullLoveIt from './TripLandingPageWhyYoullLoveIt'
import TripLandingPageAndTheresMore from './TripLandingPageAndTheresMore'
import TripLandingPageTrendingDestinations from './TripLandingPageTrendingDestinations'
import TripLandingPageFooter from './TripLandingPageFooter'
import { useTrips, useTrip } from 'tripPlanner/hooks/api/trip'
import LoggedInTripLandingHeader from './LoggedInTripLandingHeader'
import TripLandingPageHeader from './TripLandingPageHeader'
import MyTripsListGrid from '../myTrips/MyTripsListGrid'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import { partitionBy } from 'lib/array/arrayUtils'
import Divider from 'LuxKit/Divider'
import zIndex from 'styles/tools/z-index'
import { useInView } from 'react-intersection-observer'
import TabButton from 'LuxKit/Tabs/TabButton'
import { FormattedMessage } from 'react-intl'
import TripLandingPageLookingForAi from './TripLandingPageLookingForAi'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

export type TabName = 'howitworks' | 'whyyoullloveit' | 'whatourtravellerssay' | 'destinations' | 'tripplans'

const HeroImageWrapper = styled(HeroImage)`
 height: ${rem(380)};
`

const HeroImageLoadingSkeleton = styled(LoadingBox)`
 height: ${rem(380)};
 width: 100%;
`

const ScrollableButtonGroup = styled.div`
  overflow-x: auto;
  background: ${({ theme }) => theme.palette.neutral.default.eight};
  
  &::-webkit-scrollbar {
    display: none;
  }
  
`

const StickyButtonGroup = styled(ScrollableButtonGroup)`
  position: fixed;
  top:0;
  left: 50%;
  transform: translateX(-50%);
  z-index: ${zIndex.floatingPanel};
  width: 100%;
`

function LoggedOutTripPlannerLandingPage() {
  const { data: trips } = useTrips()
  const [isStickyHeaderVisible] = useState(true)
  const [activeTab, setActiveTab] = useState<TabName>('howitworks')

  const { ref: heroRef, inView: isHeroInView } = useInView({
    threshold: 0.3,
  })
  const { ref: howItWorksRef, inView: isHowItWorksInView } = useInView({
    threshold: 1,
  })

  const { ref: whyYoullLoveItRef, inView: isWhyYoullLoveItInView } = useInView({
    threshold: 0.1,
  })

  const { ref: whatOurTravellersSayRef, inView: isWhatOurTravellersSayInView } = useInView({
    threshold: 0.8,
  })

  const { ref: destinationsRef, inView: isDestinationsInView } = useInView({
    threshold: 0.3,
  })
  const { ref: tripPlansRef, inView: isTripPlansInView } = useInView({
    threshold: 0.3,
  })

  useEffect(() => {
    if (isHowItWorksInView) {
      setActiveTab('howitworks')
    } else if (isWhyYoullLoveItInView) {
      setActiveTab('whyyoullloveit')
    } else if (isWhatOurTravellersSayInView) {
      setActiveTab('whatourtravellerssay')
    } else if (isDestinationsInView) {
      setActiveTab('destinations')
    } else if (isTripPlansInView) {
      setActiveTab('tripplans')
    }
  }, [isHowItWorksInView, isWhyYoullLoveItInView, isWhatOurTravellersSayInView, isDestinationsInView, isTripPlansInView])

  const nextUpcomingTrip = useMemo(() => {
    if (!trips?.[0]) {
      return null
    }

    const now = moment()
    const [upcoming] = partitionBy(trips, (trip) => {
      return !!(trip.startDate?.isAfter(now))
    })

    if (upcoming.length === 0) {
      return trips[0]
    }

    return upcoming.reduce((minTrip, trip) =>
      trip.startDate?.isBefore(minTrip.startDate) ? trip : minTrip,
    )
  }, [trips])

  const { data: fullTrip, isLoading } = useTrip({ tripId: nextUpcomingTrip?.id })

  const handleButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const anchorId = e.currentTarget.dataset.anchorId as TabName
    setActiveTab(anchorId)
    const element = document.getElementById(anchorId)
    if (element) {
      const headerHeight = 56
      const elementPosition = element.offsetTop - headerHeight - 16
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  const shouldShowLoading = isLoading && !!nextUpcomingTrip?.id

  return (
    <div>
      {isStickyHeaderVisible && !isHeroInView && (
        <StickyButtonGroup>
          <Group direction="horizontal" horizontalAlign={nextUpcomingTrip ? 'stretch' : 'start'} verticalAlign="center" desktopHorizontalAlign="center" tabletHorizontalAlign="center">
            {!!nextUpcomingTrip && (
              <TabButton
                onClick={handleButtonClick}
                data-anchor-id="tripplans"
                size="medium"
                isActive={activeTab === 'tripplans'}
              >
                <FormattedMessage
                  id="NIAKLMnA9j5c9TsDviJY"
                  defaultMessage="Trip Plans"
                  description="Tab button label for Trip Plans section in the trip planner feature"
                />
              </TabButton>
            )}
            <TabButton
              onClick={handleButtonClick}
              data-anchor-id="howitworks"
              size="medium"
              isActive={activeTab === 'howitworks'}
            >
              <FormattedMessage
                id="DuxP2e4W4TeJHYzRpEDy"
                defaultMessage="How it works"
                description="Tab button label for How it works section in the trip planner"
              />
            </TabButton>
            {!nextUpcomingTrip && (
              <>
                <TabButton
                  onClick={handleButtonClick}
                  data-anchor-id="whyyoullloveit"
                  size="medium"
                  isActive={activeTab === 'whyyoullloveit'}
                >
                  <FormattedMessage
                    id="y3SxseQK7769kKYnjjvB"
                    defaultMessage="Why you’ll love it"
                    description="Tab button label for Why you'll love it section in the trip planner"
                  />
                </TabButton>
                <TabButton
                  onClick={handleButtonClick}
                  data-anchor-id="whatourtravellerssay"
                  size="medium"
                  isActive={activeTab === 'whatourtravellerssay'}
                >
                  <FormattedMessage
                    id="lcNTsqWR9Ce1URUmg8i3"
                    defaultMessage="What our travellers say"
                    description="Tab button label for What our travellers say section"
                  />
                </TabButton>
                <TabButton
                  onClick={handleButtonClick}
                  data-anchor-id="destinations"
                  size="medium"
                  isActive={activeTab === 'destinations'}
                >
                  <FormattedMessage
                    id="-MVIWGWdTBtVURUcyane"
                    defaultMessage="Destinations"
                    description="Tab button label for the Destinations section in the trip planner"
                  />
                </TabButton>
              </>
            )}
          </Group>
        </StickyButtonGroup>
      )}
      {shouldShowLoading && <HeroImageLoadingSkeleton />}
      {!shouldShowLoading && (
        <HeroImageWrapper
          ref={heroRef}
          id={fullTrip ? (nextUpcomingTrip?.imageId || '') : 'nimf0j6h3ldjk6c9qkj'}
          quality="best"
          brightness={fullTrip ? -50 : undefined}
          fit="center"
        >
          <LayoutContainer>
            {fullTrip && <LoggedInTripLandingHeader trip={fullTrip} />}
            {!fullTrip && <TripLandingPageHeader loggedIn />}
          </LayoutContainer>
        </HeroImageWrapper>
      )}
      {isHeroInView && (
        <LayoutContainer>
          <ScrollableButtonGroup>
            <Group direction="horizontal" horizontalAlign={nextUpcomingTrip ? 'stretch' : 'start'} verticalAlign="center" desktopHorizontalAlign="center" tabletHorizontalAlign="center">
              {!!nextUpcomingTrip && (
                <TabButton
                  onClick={handleButtonClick}
                  data-anchor-id="tripplans"
                  size="medium"
                  isActive={activeTab === 'tripplans'}
                >
                  <FormattedMessage
                    id="NIAKLMnA9j5c9TsDviJY"
                    defaultMessage="Trip Plans"
                    description="Tab button label for Trip Plans section in the trip planner feature"
                  />
                </TabButton>
              )}
              <TabButton
                onClick={handleButtonClick}
                data-anchor-id="howitworks"
                size="medium"
                isActive={activeTab === 'howitworks'}
              >
                <FormattedMessage
                  id="DuxP2e4W4TeJHYzRpEDy"
                  defaultMessage="How it works"
                  description="Tab button label for How it works section in the trip planner"
                />
              </TabButton>
              {!nextUpcomingTrip && (
                <>
                  <TabButton
                    onClick={handleButtonClick}
                    data-anchor-id="whyyoullloveit"
                    size="medium"
                    isActive={activeTab === 'whyyoullloveit'}
                  >
                    <FormattedMessage
                      id="y3SxseQK7769kKYnjjvB"
                      defaultMessage="Why you’ll love it"
                      description="Tab button label for Why you'll love it section in the trip planner"
                    />
                  </TabButton>
                  <TabButton
                    onClick={handleButtonClick}
                    data-anchor-id="whatourtravellerssay"
                    size="medium"
                    isActive={activeTab === 'whatourtravellerssay'}
                  >
                    <FormattedMessage
                      id="lcNTsqWR9Ce1URUmg8i3"
                      defaultMessage="What our travellers say"
                      description="Tab button label for What our travellers say section"
                    />
                  </TabButton>
                  <TabButton
                    onClick={handleButtonClick}
                    data-anchor-id="destinations"
                    size="medium"
                    isActive={activeTab === 'destinations'}
                  >
                    <FormattedMessage
                      id="-MVIWGWdTBtVURUcyane"
                      defaultMessage="Destinations"
                      description="Tab button label for the Destinations section in the trip planner"
                    />
                  </TabButton>
                </>
              )}
            </Group>
          </ScrollableButtonGroup>
        </LayoutContainer>
      )}
      <Divider kind="secondary" />

      <LayoutContainer>

        {!!trips && trips.length > 0 && (
          <div>
            <div id="tripplans" ref={tripPlansRef}>
              <Sheet padding="48 0 24 0">
                <Heading variant="heading2">
                  <FormattedMessage
                    id="14ZdHAC4TaWCKVF3-lQD"
                    defaultMessage="Trip plans"
                    description="Heading for Trip Plans section in the trip planner feature"
                  />
                </Heading>
              </Sheet>
            </div>
            <MyTripsListGrid
              trips={trips}
              source="trip_plans"
              isVisibleCreateNewTripTile={false}
              maxTripsToShow={6}
            />
          </div>
        )}
      </LayoutContainer>
      <CSSBreakpoint max="tablet">
        <TripLandingPageLookingForAi />
      </CSSBreakpoint>
      <div id="howitworks" ref={howItWorksRef}>
        <TripLandingPageHowItWorks />
      </div>
      <div id="whyyoullloveit" ref={whyYoullLoveItRef}>
        <TripLandingPageWhyYoullLoveIt />
      </div>
      <div id="whatourtravellerssay" ref={whatOurTravellersSayRef}>
        <TripLandingPageAndTheresMore />
      </div>
      <div id="destinations" ref={destinationsRef}>
        <TripLandingPageTrendingDestinations />
      </div>
      <TripLandingPageFooter />
    </div>
  )
}

export default LoggedOutTripPlannerLandingPage
